import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useAuth } from '../auth/UseAuth'
import { Environment } from '../environment'
import { Matomo } from '../helpers/Matomo'
import { useGetTenantQuery } from '../redux/api/customersApi'
import AllRoutes from './AllRoutes'
import { AppHeaderComponent } from './common/AppHeader/AppHeaderComponent'
import PopupArea from './common/Popup/PopupArea'
import { PopupProvider } from './common/Popup/PopupContext'
import { VersionChecker } from './common/VersionChecker/VersionChecker'
import Alert from './common/Alert/Alert'

const MAINTENANCE_NOTICE_UNTIL = new Date('2026-08-28T20:00:00+02:00').getTime()

export function App() {
  const [maintenanceNoticeVisible, setMaintenanceNoticeVisible] = useState(() => Date.now() < MAINTENANCE_NOTICE_UNTIL)
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const tenant = useGetTenantQuery(user?.tenant ?? skipToken)

  const isTenant4UPro = useMemo(() => {
    return tenant.data?.tags.some((t) => t.toLowerCase() === 'interfleetpro') === true
  }, [tenant.data])

  useEffect(() => {
    if (Environment.MatomoSiteId) {
      Matomo.init(Environment.MatomoSiteId, Environment.MatomoTrackerUrl)
    }
  }, [])

  useEffect(() => {
    Matomo.setUserId(user?.email ?? 'Anonymous')
  }, [user])

  useEffect(() => {
    Matomo.trackPageView(window.location.href, document.title)
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname !== '/') {
      return
    }

    const canViewIncoming = user?.permissions?.includes('incoming.view')
    const canViewOutgoing = user?.permissions?.includes('outgoing.view')
    if (isTenant4UPro && canViewOutgoing) {
      navigate('/outgoing')
      return
    }
    if (isTenant4UPro && canViewIncoming) {
      navigate('/incoming')
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isTenant4UPro])

  return (
    <>
      <main id="container" style={{ position: 'relative' }} className="content">
        {Environment.BackendMaintenance && (
          <h1 style={{ marginTop: '50px', marginLeft: '50px', color: '#fff' }}>
            {` ${t('BACKEND_SERVICES_UNAVAILABLE') + '. ' + t('DUE_TO_MAINTENANCE')}. `}
            <button onClick={() => window.location.reload()}>{t('PLEASE_CHECK_AGAIN')}</button>
          </h1>
        )}
        {!Environment.BackendMaintenance && (
          <PopupProvider>
            <AppHeaderComponent />
            <PopupArea />
            <AllRoutes />
          </PopupProvider>
        )}
      </main>
      <VersionChecker />
      <Alert
        visible={maintenanceNoticeVisible}
        onClose={() => setMaintenanceNoticeVisible(false)}
        title={t('MAINTENANCE_NOTICE_TITLE')}
        message={t('MAINTENANCE_NOTICE_MESSAGE')}
      />
    </>
  )
}
